// src/hooks/assistant/useConversationHydration.ts
/**
 * Conversation Hydration Hook
 * 
 * Single responsibility: Loading conversation history from API and hydrating store.
 */

import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { conversationMessagesQueryOptions, type ConversationMessage, type MessageChartData } from "@/services/queries/assistantQueries";
import { useAssistantStore } from "@/stores/assistantStore";
import type { ChatMessage } from "@/services/assistant/AssistantService";
import type { MultiAgentStreamState, ChartSpec } from "@/types/assistant";
import { createInitialMultiAgentState } from "@/types/assistant";

export interface HydrationState {
  isHydrating: boolean;
  hasData: boolean;
}

/**
 * Transform API messages to internal format with stable IDs
 */
function transformMessages(conversationId: string, data: ConversationMessage[]): ChatMessage[] {
  return data.map((m, idx) => ({
    id: `${conversationId}-msg-${idx}`,
    role: (m.role as ChatMessage["role"]) || "assistant",
    content: m.content || "",
  }));
}

/**
 * Extract chart data from message metadata
 */
function extractChartsFromMetadata(charts?: MessageChartData[]): Map<string, ChartSpec> {
  const chartMap = new Map<string, ChartSpec>();
  if (!charts?.length) return chartMap;

  for (const chart of charts) {
    if (chart.chart_ref && chart.chart_spec) {
      let spec: ChartSpec;
      if (typeof chart.chart_spec === "string") {
        try {
          spec = JSON.parse(chart.chart_spec) as ChartSpec;
        } catch {
          continue;
        }
      } else {
        spec = chart.chart_spec;
      }
      chartMap.set(chart.chart_ref, spec);
    }
  }
  return chartMap;
}

/**
 * Build multiAgentState entries from message metadata
 */
function buildMultiAgentStates(
  conversationId: string,
  data: ConversationMessage[],
  transformedMessages: ChatMessage[]
): Record<string, MultiAgentStreamState> {
  const states: Record<string, MultiAgentStreamState> = {};

  data.forEach((m, idx) => {
    if (m.role === "assistant" && m.metadata?.charts?.length) {
      const messageId = transformedMessages[idx]?.id;
      if (messageId) {
        const chartsMap = extractChartsFromMetadata(m.metadata.charts);
        if (chartsMap.size > 0) {
          const state = createInitialMultiAgentState();
          state.charts = chartsMap;
          state.conversationId = conversationId;
          states[messageId] = state;
        }
      }
    }
  });

  return states;
}

export interface UseConversationHydrationOptions {
  conversationId: string | undefined;
  authToken: string | undefined;
  limit?: number;
}

/**
 * Hook for hydrating conversation data from API
 */
export function useConversationHydration(
  options: UseConversationHydrationOptions
): HydrationState {
  const { conversationId, authToken, limit = 100 } = options;

  // Track hydration state with refs to avoid dependency issues
  const lastHydratedRef = useRef<string | null>(null);
  const isHydratingRef = useRef(false);

  // Query for conversation messages
  const { data, isLoading } = useQuery(
    conversationMessagesQueryOptions(conversationId, authToken, limit)
  );

  // Get store actions once (stable references)
  const setBaseline = useAssistantStore((s) => s.setBaseline);
  const setMultiAgentSnapshots = useAssistantStore((s) => s.setMultiAgentSnapshots);

  // Hydration effect - uses refs to track state without causing re-renders
  useEffect(() => {
    // Skip if no conversation ID or already hydrated this conversation
    if (!conversationId) {
      lastHydratedRef.current = null;
      isHydratingRef.current = false;
      return;
    }

    // Skip if we've already hydrated this conversation with this data
    const messages = data?.data;
    if (!messages?.length) {
      isHydratingRef.current = isLoading;
      return;
    }

    // Create a hash of the data to detect changes
    const lastMsg = messages[messages.length - 1];
    const dataHash = `${conversationId}-${messages.length}-${lastMsg?.content?.slice(0, 20) ?? ""}`;

    if (lastHydratedRef.current === dataHash) {
      isHydratingRef.current = false;
      return; // Already hydrated with same data
    }

    // Hydrate
    const transformedMessages = transformMessages(conversationId, messages);
    const multiAgentStates = buildMultiAgentStates(conversationId, messages, transformedMessages);

    setBaseline(conversationId, transformedMessages);
    if (Object.keys(multiAgentStates).length > 0) {
      setMultiAgentSnapshots(conversationId, multiAgentStates);
    }

    lastHydratedRef.current = dataHash;
    isHydratingRef.current = false;
  }, [conversationId, data, isLoading, setBaseline, setMultiAgentSnapshots]);

  // Return stable values
  const hasData = Boolean(data?.data?.length);
  const isHydrating = conversationId ? (isLoading && !hasData) : false;

  return { isHydrating, hasData };
}

export default useConversationHydration;
